import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    inject,
    input,
    OnInit,
    output,
    signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { ModalController, PopoverController } from '@ionic/angular';
import { endOfDay, parseISO, startOfDay, subSeconds } from 'date-fns';
import { distinctUntilChanged, filter, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { User } from '../../../../user/entities/user';
import { UserRoles } from '../../../../user/entities/user-roles';
import { mayAddTaskPlan } from '../../../../user/services/patient-menu-actions';
import { CurafidaAuthService } from '../../../../authentication/curafida-auth.service';
import { CurafidaSegmentItem } from '../../../../common/entities/curafida-segment.item';
import { PaginatedResponse, SortBy, SortOrder } from '../../../../common/entities/paginated-response';
import { SegmentType } from '../../../../common/entities/segment.type';
import { Logger, LoggingService } from '../../../../logging/logging.service';
import { CurafidaPopoverSelectionComponent } from '../../../../table/components/curafida-popover-selection/curafida-popover-selection.component';
import { CurafidaTableComponent } from '../../../../table/components/curafida-table/curafida-table.component';
import { LoadingProcess, LoadingStates } from '../../../../table/components/curafida-table/loading.process';
import { IconAdapterComponent } from '../../../../table/components/table-adapter/icon-adapter.component';
import { MultiItemAdapterComponent } from '../../../../table/components/table-adapter/multi-item-adapter.component';
import { PercentItemAdapterComponent } from '../../../../table/components/table-adapter/percent-item-adapter.component';
import { StringItemAdapterComponent } from '../../../../table/components/table-adapter/string-item-adapter.component';
import { ActionButton, ActionItemType } from '../../../../table/entities/action-menu.item';
import { CurafidaPaginationOptions } from '../../../../table/entities/curafidaPaginationOptions';
import { DateFormat, ItemType, TableConfig, TableItem, TableUpdateValue } from '../../../../table/entities/table';
import { ContainsNotPipe } from '../../../../table/pipe/array.pipe';
import { UserPreferencesService } from '../../../../user/services/user-preferences/user-preferences.service';
import { ExerciseSessionState, ExerciseSubType } from '../../../entities/exerciseSession';
import { Tag } from '../../../entities/tag/tag.entity';
import { TaskService } from '../../../services/task/task.service';
import { CarePlanerFilterModalComponent } from '../../../../common/components/patient-dossier/care-planer/care-planer-filter-modal/care-planer-filter-modal.component';
import { CaregiverTaskPreviewComponent } from '../caregiver-task-preview/caregiver-task-preview.component';
import { PatientTaskPlanListComponent } from '../patient-task-plan-list/patient-task-plan-list.component';
import { ResponsibleNamePipe } from '../task-overview/responsible-name.pipe';
import { IconFilterItem } from '../task-overview/task-overview.component';
import { SortTaskPropertyMap, TaskPreviewResource } from '../task.resource';
import { isEqual } from 'lodash-es';
import { combineLatest, merge, Subject } from 'rxjs';
import { TaskResponsibleRole } from '../task.dto';
import {
    CurafidaTableEvent,
    CurafidaTableEventAction,
} from '../../../../table/components/curafida-table/model/curafida-table.model';
import { CurafidaSegmentComponent } from '../../../../common/components/curafida-segment/curafida-segment.component';
import { ResponsibleUser } from '../../modal/task-filter-modal/responsible-user-component/model/responsible-user.model';
import {
    CurafidaSearchAndButtonButtonEvent,
    CurafidaSearchAndButtonEvent,
    CurafidaSearchAndButtonEventAction,
    CurafidaSearchAndButtonUpdateFilter,
} from '../../../../common/components/curafida-search-and-button/model/curafida-search-and-button.model';
import { CurafidaSearchAndButtonComponent } from '../../../../common/components/curafida-search-and-button/curafida-search-and-button.component';
import { QuestionnaireWorkflowService } from '../../../../questionnaire/services/questionnaire-workflow.service';
import { PatientActionWorkflowService } from '../../../../user/services/patient-action-workflow.service';
import { TaskOverviewFilterSetting } from '../task-overview/task-overview-filter.setting';
import { ButtonDefinition } from '../../../../common/entities/buttons-list-toolbar';
import { PatientSetpointsModalComponent } from '../../../../setpoints/components/patient-setpoints-modal/patient-setpoints-modal.component';

/** The name the task list of the patient record keeps its filter setting under */
export const TASK_LIST_FILTER_SETTING_NAME = 'TaskListComponent';

/** What the filter modal of a task list sets, as opposed to its tab, search, sort and paging */
const FILTER_CRITERIA_KEYS = ['tags', 'icons', 'responsibleRoles', 'responsibleUsers'] as const;

function filterCriteriaOf(
    filterSetting: TaskOverviewFilterSetting | undefined,
): Pick<TaskOverviewFilterSetting, (typeof FILTER_CRITERIA_KEYS)[number]> {
    return Object.fromEntries(FILTER_CRITERIA_KEYS.map((key) => [key, filterSetting?.[key]]));
}

@Component({
    selector: 'task-list',
    templateUrl: './task-list.component.html',
    styleUrls: ['./task-list.component.scss'],
    imports: [
        CurafidaTableComponent,
        PatientTaskPlanListComponent,
        ContainsNotPipe,
        CurafidaSegmentComponent,
        CurafidaSearchAndButtonComponent,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [ResponsibleNamePipe],
})
export class TaskListComponent implements OnInit {
    protected readonly userPreferencesService = inject(UserPreferencesService);
    protected readonly router = inject(Router);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly taskService = inject(TaskService);
    private readonly modalController = inject(ModalController);
    private readonly authService = inject(CurafidaAuthService);
    private readonly loggingService = inject(LoggingService);
    private readonly responsibleNamePipe = inject(ResponsibleNamePipe);
    private readonly popoverController = inject(PopoverController);
    private readonly questionnaireWorkflowService = inject(QuestionnaireWorkflowService);
    private readonly patientActionWorkflowService = inject(PatientActionWorkflowService);
    private readonly destroyRef = inject(DestroyRef);

    readonly isLoading = signal<LoadingStates>(LoadingProcess.initLoadingProcess());
    readonly patient = input<User>();
    /**
     * Under which name the list keeps its tab, search, sort and paging in the user preferences, and its filter
     * criteria unless {@link sharedFilterSettingName} names another setting for them. A second list on the same
     * screen (the side panel of the patient record) needs a name of its own, or paging one would page the other.
     */
    readonly filterSettingName = input(TASK_LIST_FILTER_SETTING_NAME);
    /**
     * The setting of another list to read the filter criteria (tags, icons, responsible roles and users) from and
     * save them into, so that filtering either list filters both.
     */
    readonly sharedFilterSettingName = input<string>();
    /** Emits after the user changed tasks through this list, so another list of them can reload */
    readonly tasksChanged = output<void>();
    patientTaskTableConfig: TableConfig<TaskPreviewResource[]>;
    readonly userRoles = signal<UserRoles[]>([]);
    /** Whether the logged-in user may add tasks, task plans and forms to the patient */
    readonly mayCreateTask = computed(
        () =>
            this.patientActionWorkflowService.canCreateTask(this.userRoles()) ||
            mayAddTaskPlan(this.authService.getSession().user),
    );
    UserRoles = UserRoles;
    segmentListTask: CurafidaSegmentItem<SegmentType>[] = [];
    SegmentType = SegmentType;
    displayedTaskType = signal<SegmentType>(SegmentType.ACTIVE);
    /** Use `reload()` to trigger a table reload without changing the FilterSettings */
    protected readonly reloadTable$ = new Subject<TaskOverviewFilterSetting>();
    protected previousFilterSetting: TaskOverviewFilterSetting;
    protected readonly log: Logger;
    protected defaultFilterSettings: TaskOverviewFilterSetting = {
        name: TASK_LIST_FILTER_SETTING_NAME,
        offset: 0,
        limit: 10,
        searchTerm: '',
        stateSegment: SegmentType.ACTIVE,
        sortBy: SortBy.DELAYED_TIME,
        sortByArray: [SortBy.DELAYED_TIME],
        sortOrder: SortOrder.ASC,
        filter: undefined,
    };
    /** The FilterSettings of this list, with the filter criteria of the shared setting where one is given */
    protected readonly filterSettings$ = combineLatest([
        toObservable(this.filterSettingName),
        toObservable(this.sharedFilterSettingName),
    ]).pipe(
        switchMap(([name, sharedName]) => {
            const ownSetting$ = this.userPreferencesService.settingByName<TaskOverviewFilterSetting>(name);
            if (!sharedName) return ownSetting$;
            return combineLatest([
                ownSetting$,
                this.userPreferencesService.settingByName<TaskOverviewFilterSetting>(sharedName),
            ]).pipe(
                // Without a setting of its own yet, the list first saves its defaults, just like an unshared one
                map(([own, shared]) => (own ? { ...own, ...filterCriteriaOf(shared) } : undefined)),
            );
        }),
        tap((filterSetting) => {
            if (!this.previousFilterSetting) {
                const initialFilterSetting = { ...this.defaultFilterSettings, ...filterSetting };
                this.displayedTaskType.set(initialFilterSetting.stateSegment);
                this.previousFilterSetting = structuredClone(filterSetting);
                if (filterSetting?.searchTerm && filterSetting?.searchTerm !== '') {
                    initialFilterSetting.searchTerm = '';
                    this.upsertFilterSetting(initialFilterSetting, true);
                } else {
                    this.upsertFilterSetting(initialFilterSetting);
                }
            }
        }),
        // One subscription for all readers, so the defaults above are saved once and not once per reader
        shareReplay({ bufferSize: 1, refCount: true }),
    );
    protected readonly filterCount = toSignal(
        this.filterSettings$.pipe(
            map(
                (it) =>
                    (it?.tags?.length ?? 0) +
                    (it?.icons?.length ?? 0) +
                    (it?.setpointFlags?.length ?? 0) +
                    (it?.responsibleRoles?.length ?? 0) +
                    (it?.responsibleUsers?.length ?? 0),
            ),
        ),
        { initialValue: 0 },
    );
    /** Only Emits first FilterSetting and changes to Filtersettings.
     * Workaround for: https://github.com/ReactiveX/rxjs/issues/7179
     */
    protected readonly filterSettingChanges$ = this.filterSettings$.pipe(
        filter((filterSetting) => !!filterSetting),
        map((filterSetting) => this.startOverOnChangedCriteria(filterSetting)),
        // Previous emit is not working correctly, so we use local backup
        distinctUntilChanged((_previous, current) => isEqual(this.previousFilterSetting, current)),
        tap((filterSetting) => {
            this.previousFilterSetting = structuredClone(filterSetting);
        }),
    );
    private paginationOptions: CurafidaPaginationOptions;
    private filterModalOpened = false;
    private detailModalOpened: boolean = false;
    private popoverOpened: boolean = false;

    setpointsConfigButton: ButtonDefinition = { actionName: 'openSetpointsConfig', iconName: 'settings-outline' };
    readonly buttonList = computed<ButtonDefinition[]>(() =>
        this.userRoles().includes(UserRoles.manage_therapy) ? [this.setpointsConfigButton] : [],
    );

    constructor() {
        this.log = this.loggingService.getLogger(this.constructor.name);
        this.activatedRoute.queryParams.pipe(takeUntilDestroyed()).subscribe((params) => {
            const taskId = this.activatedRoute.snapshot.queryParamMap.get('taskId');
            if (params.fromMyMedax === 'true' && taskId) {
                this.router.navigate([], {
                    queryParams: { fromMyMedax: null },
                    queryParamsHandling: 'merge',
                });
                this.taskService
                    .mymedaxSubmissionEvent$(taskId)
                    .pipe(
                        takeUntilDestroyed(),
                        finalize(() => this.openTaskDetailPage(Number(taskId)).catch(this.log.error)),
                    )
                    .subscribe();
            }
        });
    }

    ngOnInit(): void {
        this.init();
    }

    reload(): void {
        this.reloadTable$.next(null);
    }

    async upsertFilterSetting(value: Partial<TaskOverviewFilterSetting>, resetOffset: boolean = false) {
        const upsertValue = structuredClone(this.previousFilterSetting ?? this.defaultFilterSettings);

        /* First filter out null/undefined entries */
        const validUpdates = Object.fromEntries(
            Object.entries(value).filter(([_, v]) => v !== null && v !== undefined),
        );

        /* Second merge them. TypeScript handles Object.assign safely even with partials. */
        Object.assign(upsertValue, validUpdates);
        upsertValue.name = this.filterSettingName();

        if (resetOffset) {
            upsertValue.offset = 0;
        }
        if (!isEqual(this.previousFilterSetting, upsertValue)) {
            this.isLoading.set(LoadingProcess.initLoadingProcess());
            this.saveFilterSetting(upsertValue);
        }
    }

    /**
     * Saves the setting under {@link filterSettingName}, and its filter criteria into the shared setting when there is
     * one. Both go out in one update: each update sends all preferences, so a second one would undo the first.
     */
    private saveFilterSetting(filterSetting: TaskOverviewFilterSetting): void {
        const sharedName = this.sharedFilterSettingName();
        if (!sharedName) {
            this.userPreferencesService.updateFilterSettingItem(filterSetting);
            return;
        }
        const sharedSetting = this.userPreferencesService
            .userPreferences()
            ?.filterSettings?.filterSettingItem?.find((item) => item.name === sharedName);
        this.userPreferencesService.updateFilterSettingItems([
            filterSetting,
            { ...(sharedSetting ?? new TaskOverviewFilterSetting(sharedName)), ...filterCriteriaOf(filterSetting) },
        ]);
    }

    /**
     * Filter criteria changed by the other list of a shared setting show their first page, not the page this list was
     * on, which the narrowed list may not even have. The first page is saved too, or the next emission of the
     * preferences would bring the old page back.
     */
    private startOverOnChangedCriteria(filterSetting: TaskOverviewFilterSetting): TaskOverviewFilterSetting {
        const previous = this.previousFilterSetting;
        if (!previous || !filterSetting.offset) return filterSetting;
        if (isEqual(filterCriteriaOf(previous), filterCriteriaOf(filterSetting))) return filterSetting;

        const firstPage = { ...filterSetting, offset: 0 };
        this.userPreferencesService.updateFilterSettingItem(firstPage);
        return firstPage;
    }

    initTableConfig() {
        this.patientTaskTableConfig = new TableConfig<TaskPreviewResource[]>();
        this.patientTaskTableConfig.emptyListLabel = 'TASK.ANY';
        this.patientTaskTableConfig.itemSettings = [
            TableItem.create({
                id: 'dueDate',
                prop: 'dueDate',
                header: 'DUE_DATE',
                type: ItemType.ADAPTER,
                format: DateFormat.DATE_AND_HOUR_2_LINES,
                adapter: StringItemAdapterComponent,
                width: '15%',
                columnPosition: 1,
                sortBy: SortTaskPropertyMap.START_TIME,
            }),
            TableItem.create({
                id: 'appointment',
                prop: 'calendarEventStartDate',
                header: 'APPOINTMENT',
                type: ItemType.ADAPTER,
                format: DateFormat.DATE_AND_HOUR_2_LINES,
                adapter: StringItemAdapterComponent,
                width: '15%',
                columnPosition: 2,
                sortBy: SortTaskPropertyMap.CALENDAR_EVENT_START_TIME,
            }),
            TableItem.create({
                id: 'responsible',
                prop: 'responsibleName',
                header: 'RESPONSIBLE_FOR',
                type: ItemType.ADAPTER,
                adapter: StringItemAdapterComponent,
                width: '15%',
                columnPosition: 3,
                sortBy: SortTaskPropertyMap.RESPONSIBLE,
            }),
            TableItem.create({
                id: 'type',
                prop: 'exerciseSubType',
                header: 'TYPE',
                type: ItemType.ADAPTER,
                adapter: StringItemAdapterComponent,
                width: '18%',
                columnPosition: 4,
                sortBy: SortTaskPropertyMap.EXERCISE_SUB_TYPE,
                translationPrefix: 'TASK.SUB_TYPE.',
            }),
            TableItem.create({
                id: 'title',
                prop: 'title',
                header: 'TASK.SINGULAR',
                type: ItemType.ADAPTER,
                adapter: StringItemAdapterComponent,
                width: '30%',
                columnPosition: 5,
                sortBy: SortTaskPropertyMap.TITLE,
            }),
            TableItem.create({
                id: 'multiItem',
                prop: '',
                header: '',
                type: ItemType.ADAPTER,
                adapter: MultiItemAdapterComponent,
                width: '7%',
                columnPosition: 6,
                multiItemSettings: [
                    {
                        condition: (data) => data.exerciseSubType === ExerciseSubType.LEARNING,
                        itemSetting: {
                            id: 'learningProgress',
                            prop: 'learningProgress',
                            header: '',
                            type: ItemType.ADAPTER,
                            adapter: PercentItemAdapterComponent,
                        },
                    },
                    {
                        condition: () => true,
                        itemSetting: {
                            id: 'icon',
                            prop: 'icons',
                            header: '',
                            type: ItemType.ADAPTER,
                            adapter: IconAdapterComponent,
                        },
                    },
                ],
            }),
        ];
        this.patientTaskTableConfig.isOpenDetailEnable = true;
    }

    initTabs() {
        this.segmentListTask = [
            new CurafidaSegmentItem({ name: 'PLANNED_ADJECTIVE', value: SegmentType.PLANNED }),
            new CurafidaSegmentItem({ name: 'SEGMENT.CALENDAR_EVENT', value: SegmentType.CALENDAR_EVENT }),
            new CurafidaSegmentItem({ name: 'SEGMENT.ACTIVE', value: SegmentType.ACTIVE }),
            new CurafidaSegmentItem({ name: 'CLOSED', value: SegmentType.INACTIVE }),
            new CurafidaSegmentItem({ name: 'CANCELLED', value: SegmentType.CANCELLED }),
            new CurafidaSegmentItem({ name: 'EXPIRED', value: SegmentType.EXPIRED }),
        ];
        if (this.userRoles().includes(UserRoles.read_task_plan)) {
            this.segmentListTask.push(new CurafidaSegmentItem({ name: 'TASK.PLAN.PLURAL', value: SegmentType.PLAN }));
        }
        this.paginationOptions = new CurafidaPaginationOptions(this.patientTaskTableConfig.itemSettings);
        for (const segment of this.segmentListTask) {
            const segmentSettings = this.taskService.setStateConfiguration(segment.value);
            this.paginationOptions.set(
                { segment: segment.value },
                {
                    sortBy: segmentSettings.sortBy,
                    sortOrder: segmentSettings.sortOrder,
                    offset: 0,
                    tableColumnId: segmentSettings.tableColumnId,
                },
            );
        }
    }

    /**
     * Fetches the page of tasks the given setting describes. Writes nothing itself, so a superseded load can be
     * dropped by the caller; a failed fetch resolves to an empty, unsuccessful result instead of rejecting.
     */
    async getPatientTaskList(
        filterSetting: TaskOverviewFilterSetting,
    ): Promise<{ list: PaginatedResponse<TaskPreviewResource[]>; success: boolean }> {
        const stateConfiguration = this.taskService.setStateConfiguration(this.displayedTaskType());
        try {
            const paginatedResponse = await this.taskService.fetchTasks(this.patient().username, {
                exerciseSessionStates: stateConfiguration.taskStates,
                exerciseSessionUserStates: stateConfiguration.taskUserStates,
                sortBy: filterSetting?.sortByArray as unknown as SortTaskPropertyMap[],
                sortOrder: filterSetting?.sortOrder,
                limit: filterSetting?.limit,
                offset: filterSetting?.offset,
                textSearchTerm: filterSetting?.searchTerm,
                onlyWithCalendarEvent: this.displayedTaskType() === SegmentType.CALENDAR_EVENT,
                includeStateChanges: true,
                tagUuids: filterSetting?.tags?.map((i) => i.uuid),
                iconFilter: filterSetting?.icons,
                setpointFlags: filterSetting?.setpointFlags,
                responsibleRoles: filterSetting?.responsibleRoles,
                responsibleUsernames: filterSetting?.responsibleUsers?.map((user) => user.username),
            });
            paginatedResponse.items.forEach((userTask) => {
                this.setTaskDueDate(userTask);
                userTask.responsibleName = this.responsibleNamePipe.transform(userTask.responsibility);
                userTask.calendarEventStartDate = userTask.calendarEvent?.startTime;
                if (userTask.exerciseSubType === ExerciseSubType.LEARNING) {
                    userTask.learningProgress =
                        userTask.lessons.filter((lesson) => lesson.isFinished).length / userTask.lessons.length;
                }
            });
            return { list: paginatedResponse, success: true };
        } catch (err) {
            this.log.error('Error in getPatientTaskList', err);
            return { list: PaginatedResponse.init([]), success: false };
        }
    }

    async openTaskDetailPage(taskId: number) {
        if (this.detailModalOpened) {
            return;
        }
        this.detailModalOpened = true;
        try {
            const task = await this.taskService.fetchTask(taskId);
            if (task.exerciseSessionState === ExerciseSessionState.FINISHED) {
                this.patientTaskTableConfig.list.items.some((item) => item.id === taskId) &&
                    this.reloadTable$.next(null);
            }
            const modal = await this.modalController.create({
                component: CaregiverTaskPreviewComponent,
                id: 'CaregiverTaskPreviewComponent',
                cssClass: 'task-preview-modal',
                componentProps: { initialTaskResource: task, patient: this.patient() },
            });
            await modal.present();
            const dismissData = await modal.onDidDismiss();
            if (dismissData) {
                this.reloadAfterChange();
            }
        } finally {
            this.detailModalOpened = false;
        }
    }

    async buttonClick(actionType?: CurafidaSearchAndButtonButtonEvent): Promise<void> {
        if (actionType?.type === this.setpointsConfigButton.actionName) {
            await this.openSetpointsConfig();
        } else {
            await this.openAddModal(actionType?.event);
        }
    }

    async openSetpointsConfig(): Promise<void> {
        const modal = await this.modalController.create({
            component: PatientSetpointsModalComponent,
            cssClass: 'full-width-modal',
            componentProps: { patient: this.patient() },
        });
        await modal.present();
    }

    private async openAddModal(event?: MouseEvent): Promise<void> {
        if (this.popoverOpened) {
            return;
        }
        this.popoverOpened = true;
        const actionMenuItems = [];
        if (mayAddTaskPlan(this.authService.getSession().user)) {
            actionMenuItems.push(new ActionButton(ActionItemType.BUTTON, 'TASK.PLAN.ADD', 'taskPlan'));
        }
        if (this.patientActionWorkflowService.canCreateTask(this.userRoles())) {
            actionMenuItems.push(
                new ActionButton(ActionItemType.BUTTON, 'TASK.ADD', 'task'),
                new ActionButton(ActionItemType.BUTTON, 'UPLOAD_NEW_FORM', 'form'),
            );
        }
        const popover = await this.popoverController.create({
            component: CurafidaPopoverSelectionComponent,
            cssClass: 'my-custom-class',
            translucent: true,
            event: event,
            componentProps: {
                actionItems: actionMenuItems,
            },
        });
        await popover.present();
        const { data } = await popover.onDidDismiss().then((data) => {
            this.popoverOpened = false;
            return data;
        });
        if (data) {
            switch (data) {
                case 'task':
                    await this.openTaskCreationModal();
                    break;
                case 'taskPlan':
                    await this.openTaskPlanCreationModal();
                    break;
                case 'form':
                    await this.openUploadForm();
                    break;
            }
        }
    }

    async openTaskPlanCreationModal(): Promise<void> {
        // The same rights openAddModal() offers the action with
        if (mayAddTaskPlan(this.authService.getSession().user)) {
            const taskPlan = await this.patientActionWorkflowService.openTaskPlanModal(this.patient());
            if (taskPlan) {
                await this.setTaskSegmentType(SegmentType.PLAN);
                this.tasksChanged.emit();
            }
        }
    }

    async openTaskCreationModal(): Promise<void> {
        await this.patientActionWorkflowService.openTaskModal(this.patient());
        this.reloadAfterChange();
    }

    async openUploadForm(): Promise<void> {
        await this.questionnaireWorkflowService.startQuestionnaireWorkflow(this.patient());
        this.reloadAfterChange();
    }

    private reloadAfterChange(): void {
        this.reloadTable$.next(null);
        this.tasksChanged.emit();
    }

    setTaskDueDate(task: TaskPreviewResource) {
        if (task.stateChanges) {
            task.stateChanges.sort((a, b) => b.id - a.id); // sort decending by id
            switch (this.displayedTaskType()) {
                case SegmentType.PLANNED:
                    task.dueDate = task.appointment?.startTime
                        ? startOfDay(parseISO(task.appointment?.startTime)).toISOString()
                        : null;
                    break;
                case SegmentType.INACTIVE:
                    const finishedStateChange = task.stateChanges.find(
                        (sc) => sc.newState === ExerciseSessionState.FINISHED,
                    );
                    task.dueDate = finishedStateChange?.created_at
                        ? parseISO(finishedStateChange.created_at.toString()).toISOString()
                        : null;
                    task.finished_at = finishedStateChange?.created_at
                        ? new Date(finishedStateChange?.created_at)
                        : null;
                    break;
                case SegmentType.CANCELLED:
                    const cancelledStateChange = task.stateChanges.find((sc) =>
                        [ExerciseSessionState.CANCELLED, ExerciseSessionState.PATIENT_CANCELLED].includes(sc.newState),
                    );
                    task.dueDate = cancelledStateChange?.created_at
                        ? parseISO(cancelledStateChange.created_at.toString()).toISOString()
                        : null;
                    break;
                case SegmentType.EXPIRED:
                    const expiredStateChange = task.stateChanges.find(
                        (sc) => sc.newState === ExerciseSessionState.FINISHED,
                    );
                    task.dueDate = expiredStateChange?.created_at
                        ? parseISO(expiredStateChange.created_at.toString()).toISOString()
                        : null;
                    break;
                default:
                    task.dueDate = task.appointment?.delayedTime
                        ? endOfDay(subSeconds(parseISO(task.appointment?.delayedTime), 1)).toISOString()
                        : null;
                    break;
            }
        }
    }

    setTableHeaders() {
        const tableConfigDueDate = this.patientTaskTableConfig.itemSettings.find((tc) => tc.id === 'dueDate');
        switch (this.displayedTaskType()) {
            case SegmentType.PLANNED:
                tableConfigDueDate.header = 'HEADER.PLANNED_AT';
                tableConfigDueDate.sortBy = SortTaskPropertyMap.DELAYED_TIME;
                break;
            case SegmentType.INACTIVE:
                tableConfigDueDate.header = 'HEADER.FINISHED_AT';
                tableConfigDueDate.sortBy = SortTaskPropertyMap.LATEST_STATE_CHANGE;
                break;
            case SegmentType.CANCELLED:
                tableConfigDueDate.header = 'HEADER.CANCELLED_AT';
                tableConfigDueDate.sortBy = SortTaskPropertyMap.LATEST_STATE_CHANGE;
                break;
            case SegmentType.EXPIRED:
                tableConfigDueDate.header = 'HEADER.EXPIRED_AT';
                tableConfigDueDate.sortBy = SortTaskPropertyMap.LATEST_STATE_CHANGE;
                break;
            default:
                tableConfigDueDate.header = 'HEADER.DUE_AT';
                tableConfigDueDate.sortBy = SortTaskPropertyMap.DELAYED_TIME;
                break;
        }
    }

    async setTaskSegmentType(event: SegmentType) {
        this.displayedTaskType.set(event);
        const receivedPaginationOptions = this.paginationOptions.get({
            segment: event,
        });
        await this.upsertFilterSetting(
            {
                sortByArray: receivedPaginationOptions.sortBy as SortBy[],
                sortOrder: receivedPaginationOptions.sortOrder,
                stateSegment: event,
            },
            true,
        ).then(() => {
            if (event !== SegmentType.PLAN) {
                this.setTableHeaders();
            }
        });
    }

    async openFilterModal() {
        if (this.filterModalOpened) {
            return;
        }
        this.filterModalOpened = true;
        const modal = await this.modalController.create({
            component: CarePlanerFilterModalComponent,
            cssClass: 'modal-tag-list-css',
            componentProps: {
                mode: 'TASK',
                filterSettingName: this.sharedFilterSettingName() ?? this.filterSettingName(),
            },
        });
        await modal.present();
        await modal.onDidDismiss().then(({ data }) => {
            this.filterModalOpened = false;
            /* Dismissed through the toolbar instead of the apply button: nothing was picked. */
            if (data) this.upsertFilterSetting(data, true);
        });
    }

    async processTableEvent(value: CurafidaTableEvent) {
        switch (value.action) {
            case CurafidaTableEventAction.UPDATE_SORT_ORDER:
            case CurafidaTableEventAction.UPDATE_TABLE: {
                const changes = value.payload as TableUpdateValue;
                await this.upsertFilterSetting({
                    offset: changes.offset,
                    limit: changes.limit,
                    sortByArray: changes.sortByArray as SortBy[],
                    sortOrder: changes.sortOrder,
                });
                break;
            }
        }
    }

    async processFilterUpdate(value: CurafidaSearchAndButtonEvent) {
        switch (value.action) {
            case CurafidaSearchAndButtonEventAction.UPDATE_FILTER: {
                const changes = value.payload as CurafidaSearchAndButtonUpdateFilter;
                await this.upsertFilterSetting({ searchTerm: changes.searchTerm }, true);
                break;
            }
        }
    }

    private init(): void {
        this.userRoles.set(this.authService.getSession().user.roles);
        this.initTableConfig();
        this.initTabs();
        this.initFilterSettings();
        this.setTableHeaders();
    }

    private initFilterSettings(): void {
        // Every change to FilterSettings or a reloadTable emit trigger a reload of the Table
        merge(
            this.filterSettingChanges$.pipe(filter((filterSetting) => filterSetting.stateSegment !== SegmentType.PLAN)),
            this.reloadTable$.pipe(map(() => this.previousFilterSetting)),
        )
            .pipe(
                tap(() => this.isLoading.set(LoadingProcess.initLoadingProcess())),
                /* Only the latest load may reach the table; a slower, superseded one is dropped. */
                switchMap((filterSetting) => this.getPatientTaskList(filterSetting)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(({ list, success }) => {
                this.patientTaskTableConfig.list = list;
                this.isLoading.set(
                    success
                        ? LoadingProcess.finishedSuccessfullyLoadingProcess()
                        : LoadingProcess.finishedWithErrorLoadingProcess(),
                );
            });
    }
}
